/**
 * VERSIONES CONGELADAS DEL CUADERNO (CQ4a · PLAN §3; RD 1311/2012 art. 16: 3 años).
 *
 * Cada descarga del PDF (o del Excel) se CONGELA antes de entregarse:
 *   1. el fichero se sube al bucket PRIVADO `cuaderno-exports`, en la carpeta de
 *      la empresa (`<empresa>/<titular|empresa>/<año>/…`), sin sobrescribir;
 *   2. se inserta una fila en `cuaderno_exports` con el sha256 del FICHERO, el
 *      sha256 de los DATOS (el CuadernoData en JSON canónico) y los datos mismos.
 *      El nº de versión, el año, el titular y quién la generó los pone el
 *      servidor (trigger), no este código.
 * Ni la fila ni el fichero se pueden cambiar ni borrar desde la app.
 *
 * Por qué el sha256 de los DATOS además del del fichero: el PDF lleva la fecha de
 * creación dentro, así que dos PDF del mismo cuaderno no tienen el mismo hash. Para
 * avisar de «los datos han cambiado desde la versión N» se comparan los datos.
 *
 * Por qué no `pdf-exports`: esa carpeta es POR USUARIO, temporal (la usa el visor
 * nativo, `openPdfInNativeViewer`) y cada uno puede borrar lo suyo. Una versión
 * congelada es de la EMPRESA y no se borra.
 */
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { CuadernoData, type CuadernoDataInput } from "./types";

export const BUCKET_CUADERNO = "cuaderno-exports";

export const MIME_CUADERNO = {
  pdf: "application/pdf",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
} as const;
export type FormatoCuaderno = keyof typeof MIME_CUADERNO;

/** sha256 en hexadecimal (WebCrypto: navegador, app nativa y vitest). */
export async function sha256Hex(entrada: Uint8Array | ArrayBuffer | string): Promise<string> {
  const bytes =
    typeof entrada === "string" ? new TextEncoder().encode(entrada) : entrada instanceof Uint8Array ? entrada : new Uint8Array(entrada);
  const hash = await crypto.subtle.digest("SHA-256", bytes as BufferSource);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** JSON con las claves ordenadas y sin `undefined`: el mismo cuaderno da siempre el mismo texto. */
export function jsonCanonico(v: unknown): string {
  const norm = (x: unknown): unknown => {
    if (Array.isArray(x)) return x.map(norm);
    if (x && typeof x === "object") {
      const o = x as Record<string, unknown>;
      return Object.fromEntries(
        Object.keys(o)
          .filter((k) => o[k] !== undefined)
          .sort()
          .map((k) => [k, norm(o[k])]),
      );
    }
    return x;
  };
  return JSON.stringify(norm(v));
}

/** Los datos tal y como se imprimen (normalizados por el esquema) y su huella. */
export async function huellaDatos(data: CuadernoDataInput): Promise<{ datos: CuadernoData; sha256: string }> {
  const datos = CuadernoData.parse(data);
  return { datos, sha256: await sha256Hex(jsonCanonico(datos)) };
}

/** «<empresa>/<titular|empresa>/<año>/<aaaammddThhmmss>-<sha12>.<ext>». */
export function rutaVersion(o: {
  companyId: string;
  titularLocationId: string | null;
  year: number;
  sha256: string;
  formato: FormatoCuaderno;
  ahora?: Date;
}): string {
  const sello = (o.ahora ?? new Date()).toISOString().replace(/[-:]/g, "").replace(/\.\d+Z$/, "Z");
  return `${o.companyId}/${o.titularLocationId ?? "empresa"}/${o.year}/${sello}-${o.sha256.slice(0, 12)}.${o.formato}`;
}

export interface VersionCongelada {
  id: string;
  version: number;
  format: FormatoCuaderno;
  sha256: string;
  data_sha256: string;
  storage_path: string;
  generated_at: string;
  draft_version: number;
}

type Sb = SupabaseClient<Database>;

/** El error de PostgREST tal cual (para `leerErrorCambio`: message, details, code). */
export interface ErrorRpc {
  message?: string;
  details?: string | null;
  code?: string;
}

/**
 * El id del borrador de ese titular y año, CREÁNDOLO si no existe: la RPC
 * `ensure_cuaderno_draft` (el borrador solo se escribe por RPC; la tabla no deja
 * INSERT a nadie de la app). Es la ÚNICA forma de conseguirlo para escribir: la
 * usan la descarga (`congelarVersion`) y «Aplicar» de la tarjeta de cambios
 * (`aplicarCambio.ts`). No lanza: devuelve el error de la RPC para enseñarlo.
 */
export async function abrirBorrador(
  // Cualquier cliente con `.rpc` (el del navegador o uno de mentira en las pruebas).
  supabase: Pick<Sb, "rpc">,
  o: { companyId: string; titularLocationId: string | null; year: number },
): Promise<{ id: string | null; error: ErrorRpc | null }> {
  const { data, error } = await supabase.rpc("ensure_cuaderno_draft", {
    p_company_id: o.companyId,
    p_titular_location_id: o.titularLocationId,
    p_year: o.year,
  });
  if (error) return { id: null, error };
  return data ? { id: String(data), error: null } : { id: null, error: { message: "CUADERNO_SIN_BORRADOR" } };
}

/** Como `abrirBorrador`, pero lanza (la descarga no entrega sin borrador). */
export async function asegurarBorrador(supabase: Sb, o: { companyId: string; titularLocationId: string | null; year: number }): Promise<string> {
  const { id, error } = await abrirBorrador(supabase, o);
  if (!id) throw new Error(`cuaderno: no se ha podido abrir el borrador (${error?.message ?? "sin id"})`);
  return id;
}

/** La última versión congelada de ese titular y año (para avisar si los datos cambiaron). */
export async function ultimaVersion(
  supabase: Sb,
  o: { companyId: string; titularLocationId: string | null; year: number },
): Promise<VersionCongelada | null> {
  let q = supabase
    .from("cuaderno_exports")
    .select("id, version, format, sha256, data_sha256, storage_path, generated_at, draft_version")
    .eq("company_id", o.companyId)
    .eq("year", o.year);
  q = o.titularLocationId ? q.eq("titular_location_id", o.titularLocationId) : q.is("titular_location_id", null);
  const { data, error } = await q.order("version", { ascending: false }).limit(1).maybeSingle();
  if (error) throw new Error(`cuaderno: no se han podido leer las versiones (${error.message})`);
  return (data as VersionCongelada) ?? null;
}

export interface Congelar {
  companyId: string;
  titularLocationId: string | null;
  year: number;
  draftId?: string | null;
  /**
   * La versión del borrador con la que se montaron `data` (`borrador.version`, 0 si no
   * había borrador). El trigger la compara con la del borrador y, si otro «Aplicar»
   * la cambió entretanto, rechaza con CUADERNO_EXPORT_DESFASADA (M4).
   */
  draftVersion: number;
  formato: FormatoCuaderno;
  /** El fichero TAL CUAL se va a entregar. */
  bytes: Uint8Array | Blob;
  data: CuadernoDataInput;
  cobertura?: unknown;
  /** Plantilla con la que se estampó (`CUADERNO_MODELO`). */
  modelo?: { nombre: string; sha256: string } | null;
  /**
   * Fincas incluidas si quien descarga está acotado (A16); null = todas las del titular.
   * SOLO informativo: el trigger de `cuaderno_exports` lo vuelve a CALCULAR con el
   * alcance de quien genera la versión (A3) y lo que se manda aquí no cuenta.
   */
  alcanceFincas?: string[] | null;
  ahora?: Date;
}

/** El borrador cambió entre montar los datos y congelarlos (M4): hay que volver a montarlos. */
export class BorradorCambiado extends Error {
  constructor() {
    super("cuaderno: el borrador ha cambiado desde que se montaron los datos (CUADERNO_EXPORT_DESFASADA)");
    this.name = "BorradorCambiado";
  }
}

/**
 * Congela una descarga. Si algo falla, lanza y NO se entrega: una versión que se
 * firma tiene que estar guardada. (Si el fichero sube y la fila no entra, queda
 * un fichero huérfano en la carpeta de la empresa: se ve y no engaña a nadie.)
 */
export async function congelarVersion(supabase: Sb, c: Congelar): Promise<VersionCongelada> {
  const draftId = c.draftId ?? (await asegurarBorrador(supabase, c));
  const bytes = c.bytes instanceof Blob ? new Uint8Array(await c.bytes.arrayBuffer()) : c.bytes;
  const [sha256, huella] = await Promise.all([sha256Hex(bytes), huellaDatos(c.data)]);
  const storage_path = rutaVersion({ ...c, sha256, formato: c.formato });

  const subida = await supabase.storage.from(BUCKET_CUADERNO).upload(storage_path, bytes, {
    contentType: MIME_CUADERNO[c.formato],
    upsert: false,
  });
  if (subida.error) throw new Error(`cuaderno: no se ha podido guardar el fichero (${subida.error.message})`);

  const { data, error } = await supabase
    .from("cuaderno_exports")
    .insert({
      company_id: c.companyId,
      draft_id: draftId,
      // Año y versión los pone el trigger con los del borrador; se mandan porque la columna los exige.
      year: c.year,
      version: 1,
      // M4: la del borrador con la que se montaron los datos; el trigger la comprueba.
      draft_version: c.draftVersion,
      format: c.formato,
      storage_path,
      sha256,
      data_sha256: huella.sha256,
      data: huella.datos as unknown as Database["public"]["Tables"]["cuaderno_exports"]["Insert"]["data"],
      modelo: (c.modelo ?? null) as Database["public"]["Tables"]["cuaderno_exports"]["Insert"]["modelo"],
      cobertura: (c.cobertura ?? null) as Database["public"]["Tables"]["cuaderno_exports"]["Insert"]["cobertura"],
      alcance_fincas: c.alcanceFincas ?? null,
    })
    .select("id, version, format, sha256, data_sha256, storage_path, generated_at, draft_version")
    .single();
  if (error?.message?.includes("CUADERNO_EXPORT_DESFASADA")) throw new BorradorCambiado();
  if (error || !data) throw new Error(`cuaderno: no se ha podido congelar la versión (${error?.message ?? "sin fila"})`);
  return data as VersionCongelada;
}
